import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

interface RoleChangeModalProps {
  open: boolean;
  onClose: () => void;
  user: { id: string; name: string; role: string } | null;
  onConfirm: (userId: string, newRole: 'admin' | 'user') => void;
  isLoading?: boolean;
  error?: string | null;
}

export function RoleChangeModal({
  open,
  onClose,
  user,
  onConfirm,
  isLoading = false,
  error = null,
}: RoleChangeModalProps) {
  const [selectedRole, setSelectedRole] = useState<'admin' | 'user'>('user');

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role as 'admin' | 'user');
    }
  }, [user]);

  const handleConfirm = () => {
    if (user) {
      onConfirm(user.id, selectedRole);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>역할 변경</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {user && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              사용자: <strong>{user.name}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="role-select-label">새 역할</InputLabel>
              <Select
                labelId="role-select-label"
                value={selectedRole}
                label="새 역할"
                onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'user')}
                disabled={isLoading}
              >
                <MenuItem value="admin">관리자</MenuItem>
                <MenuItem value="user">일반 사용자</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          취소
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || selectedRole === user?.role}
        >
          {isLoading ? <CircularProgress size={24} /> : '변경'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
